import React, { Component } from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get } from "lodash";
import {
  getReauthorizationLinkToken,
  getIndividualReauthorizationLinkToken,
  updateLinkToken,
} from "actions/bankActions";
import { getAllEmployerAccounts } from "actions/employerActions";
import { getAllAccounts } from "actions/accountActions";
import { bankConstants } from "actions/types";
import { createLoadingSelector } from "store/selectors";
import { isEmployerSelector } from "store/selectors/user";
import type { RootState } from "types/state";

import PlaidLinkTokenStorage from "utils/PlaidLinkTokenStorage";
import Alert from "components/Alert";
import Button from "components/Button";

// window.Plaid is injected by the Plaid Link script (loaded in index.html) and
// has no @types package; describe just the surface this component touches.
interface PlaidHandler {
  open: () => void;
  destroy: () => void;
}
interface PlaidLinkOnExitError {
  error_code?: string;
}
interface PlaidGlobal {
  create: (config: {
    token: string;
    onExit: (err: PlaidLinkOnExitError | null, metadata: unknown) => void;
    onSuccess: (token: string, metadata: unknown) => void | Promise<void>;
  }) => PlaidHandler;
}

interface ReAuthPlaidLinkOwnProps {
  label?: string;
  style?: React.CSSProperties;
  // render receives the component instance (this); the class is declared
  // below, so this is a type-only forward reference.
  // eslint-disable-next-line no-use-before-define -- type-only forward ref to the class below
  render?: (instance: ReAuthPlaidLink) => React.ReactNode;
  onSuccess?: (token: string, metadata: unknown, userLegalName: string) => void;
  onExit?: (metadata: unknown) => void;
  bank?: { id?: string } | null;
}

const loadingSelector = createLoadingSelector([
  bankConstants.GET_REAUTHORIZATION_LINK_TOKEN,
  bankConstants.GET_INDIVIDUAL_REAUTHORIZATION_LINK_TOKEN,
  bankConstants.UPDATE_LINK_TOKEN,
]);

const mapStateToProps = (state: RootState) => {
  // profile carries arbitrary backend keys (Record<string, unknown>), so
  // firstName/lastName are unknown — cast at the boundary for interpolation.
  const userLegalName = `${state.user.profile.firstName as string} ${
    state.user.profile.lastName as string
  }`;
  return {
    userLegalName,
    userId: state.user.id,
    isFetching: loadingSelector(state),
    isEmployer: isEmployerSelector(state),
  };
};

const mapDispatchToProps = {
  getReauthorizationLinkToken,
  getIndividualReauthorizationLinkToken,
  updateLinkToken,
  getAllEmployerAccounts,
  getAllAccounts,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type ReAuthPlaidLinkProps = PropsFromRedux & ReAuthPlaidLinkOwnProps;

interface ReAuthPlaidLinkState {
  linkLoaded: boolean;
  error: string | null;
  isOpen: boolean;
}

class ReAuthPlaidLink extends Component<
  WithApolloClient<ReAuthPlaidLinkProps>,
  ReAuthPlaidLinkState
> {
  static defaultProps = {
    style: {
      padding: "6px 4px",
      outline: "none",
      background: "#FFFFFF",
      border: "2px solid #F1F1F1",
      borderRadius: "4px",
    },
  };

  _isMounted = false;
  plaidHandler: PlaidHandler | null = null;

  constructor(props: WithApolloClient<ReAuthPlaidLinkProps>) {
    super(props);
    this.state = {
      linkLoaded: false,
      error: null,
      isOpen: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (!this.state.isOpen) {
      // userId is string | null from redux state; clearTokenContext expects a
      // string. Pre-existing nullability gap — passed through unchanged.
      PlaidLinkTokenStorage.clearTokenContext(this.props.userId as string);
    }
  }

  onExit = async (err: PlaidLinkOnExitError | null, metadata: unknown) => {
    if (err != null && err.error_code === "INVALID_LINK_TOKEN") {
      this.plaidHandler!.destroy();
      // Recreate the handler with a fresh link token. The method is
      // createReAuthPlaidLink — calling the (nonexistent) createPlaidLink here
      // threw at runtime on every invalid-link-token retry.
      await this.createReAuthPlaidLink();
    }

    if (err != null) {
      this.setState({ error: "Failed to initialize plaid" });
      console.error({ message: "Failed to initialize plaid", err });
    }
    // userId is string | null from redux state; clearTokenContext expects a
    // string. Pre-existing nullability gap — passed through unchanged.
    PlaidLinkTokenStorage.clearTokenContext(this.props.userId as string);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- preserving the original short-circuit guard verbatim; behavior unchanged
    this.props.onExit && this.props.onExit(metadata);
  };

  createReAuthPlaidLink = async () => {
    try {
      const accountId = get(this.props.bank, "id");

      let reAuthLinkToken;

      // accountId comes from get(bank, "id") = string | undefined; the action
      // creators expect string. Pre-existing gap — passed through unchanged.
      if (this.props.isEmployer) {
        reAuthLinkToken = await this.props.getReauthorizationLinkToken(
          this.props.client as ApolloClient<object>,
          accountId as string
        );
      } else {
        reAuthLinkToken =
          await this.props.getIndividualReauthorizationLinkToken(
            this.props.client as ApolloClient<object>,
            accountId as string
          );
      }

      const token = get(reAuthLinkToken, "data.linkToken");

      if (!token) {
        throw new Error("Link token is missing.");
      }

      const plaidConfig = {
        token,
        onExit: (err: PlaidLinkOnExitError | null, metadata: unknown) =>
          this.onExit(err, metadata),
        onSuccess: async (token: string, metadata: unknown) => {
          // userId is string | null; accountId is string | undefined. Both
          // pre-existing nullability gaps — passed through unchanged.
          PlaidLinkTokenStorage.clearTokenContext(this.props.userId as string);

          await this.props.updateLinkToken(
            this.props.client as ApolloClient<object>,
            accountId as string
          );

          if (this.props.isEmployer) {
            await this.props.getAllEmployerAccounts(
              this.props.client as ApolloClient<object>
            );
          } else {
            await this.props.getAllAccounts(
              this.props.client as ApolloClient<object>
            );
          }

          // onSuccess is an optional prop but invoked unconditionally here
          this.props.onSuccess!(token, metadata, this.props.userLegalName);
        },
      };

      // window.Plaid is injected by the Plaid Link script and is not on the
      // Window type; reach for it through a cast at the boundary.
      this.plaidHandler = (
        window as unknown as { Plaid: PlaidGlobal }
      ).Plaid.create(plaidConfig);
      if (this._isMounted) {
        this.setState({ linkLoaded: true });
      }
      return true;
    } catch (error) {
      this.setState({
        error: (error as Error).message || "Failed to load bank information.",
      });
      console.error("Error in createReAuthPlaidLink:", error);
      throw error;
    }
  };

  handleOnClick = async () => {
    // Gate open() on createReAuthPlaidLink's return value, not on
    // this.state.linkLoaded. Under React 19 automatic batching the setState in
    // createReAuthPlaidLink hasn't flushed by the time this synchronous line
    // runs, so reading this.state.linkLoaded here sees the stale `false` and
    // the Plaid box never opens. (Pre-React-18 this setState flushed
    // synchronously after the await, so the old guard happened to work.)
    const linkLoaded = await this.createReAuthPlaidLink();
    if (linkLoaded) {
      this.plaidHandler!.open();
      this.setState({ isOpen: true });
    }
  };

  render() {
    return (
      <>
        {(this.props.render && this.props.render(this)) || (
          <Button
            name="submit"
            color="action"
            size="sm"
            disabled={this.state.linkLoaded}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises -- handleOnClick is async; original wired it fire-and-forget, unchanged
            onClick={this.handleOnClick}
            btnLabel="Authenticate Bank"
            loading={this.props.isFetching}
            withArrow
          />
        )}
        {this.state.error && <Alert msg={this.state.error} type="error" />}
      </>
    );
  }
}

export default connector(withApollo<ReAuthPlaidLinkProps>(ReAuthPlaidLink));
